import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { SubuserPermissionsService, ClientPageItem } from '@services/subuser-permissions.service';
import { SharedService } from '@services/shared.service';

/** One rendered table row, carrying its place in the parent/child tree. */
export interface PageRow {
  page: ClientPageItem;
  level: number;
  childCount: number;
  isLastChild: boolean;
}

export interface ClientPageForm {
  id: number;
  page_name: string;
  page_key: string;
  url: string;
  parent_id: number;
}

@Component({
  selector: 'app-client-pages',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NzModalModule, NzSelectModule, NzTableModule, NzToolTipModule],
  templateUrl: './client-pages.component.html',
  styleUrls: ['./client-pages.component.scss'],
})
export class ClientPagesComponent implements OnInit {
  private permissionsService = inject(SubuserPermissionsService);
  private sharedService = inject(SharedService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  isLoading = false;
  searchTerm = '';
  pages: ClientPageItem[] = [];
  /** Parent page ids whose children are hidden. */
  collapsedParents = new Set<number>();

  // Create / Edit modal (shared form)
  isFormModalVisible = false;
  isEditMode = false;
  isSaving = false;
  form: ClientPageForm = this.emptyForm();
  formErrors: Partial<Record<keyof ClientPageForm, string>> = {};
  serverError = '';
  keyTouched = false;

  // Import defaults modal
  isImportModalVisible = false;
  isImporting = false;
  importProgress = '';

  // Delete modal
  isDeleteModalVisible = false;
  deletingPage: ClientPageItem | null = null;
  isDeleting = false;

  /**
   * Display name for a page. Known pages (page_key present in client-pages.page-names)
   * show the localized label so the table matches the sidebar; pages the client added
   * themselves fall back to the name stored on the server.
   */
  displayName(page: ClientPageItem): string {
    const key = (page.page_key || '').trim().toLowerCase();
    if (key) {
      const translated = this.lookup(`client-pages.page-names.${key}`);
      if (translated) return translated;
    }
    return page.page_name || '';
  }

  /** Look up a translation, returning null when the key is absent. */
  private lookup(path: string): string | null {
    const value = this.translateService.instant(path);
    // ngx-translate echoes the path back when the key is missing.
    return value && value !== path ? value : null;
  }

  /**
   * Readable label for a page key. The real page_key is what gets stored and
   * matched against backend permissions — this only changes what is rendered.
   */
  displayKey(page: ClientPageItem): string {
    const raw = (page.page_key || '').trim();
    if (!raw) return '';
    return this.lookup(`client-pages.page-names.${raw.toLowerCase()}`) ?? raw;
  }

  /**
   * Readable label for a route, translated one segment at a time so unknown
   * segments (a client's own page) still render. The real url is untouched —
   * routing and the edit form keep using page.url.
   */
  displayUrl(page: ClientPageItem): string {
    const url = (page.url || '').trim();
    if (!url) return '';
    const parts = url.split('/').filter(Boolean);
    if (parts.length === 0) return url;
    const labels = parts.map(
      (seg) => this.lookup(`client-pages.url-segments.${seg.toLowerCase()}`) ?? seg
    );
    return labels.join(' / ');
  }

  /** Translate a client-pages key, with optional interpolation params. */
  private t(key: string, params?: Record<string, any>): string {
    return this.translateService.instant(`client-pages.${key}`, params);
  }

  async ngOnInit(): Promise<void> {
    await this.loadPages();
  }

  private emptyForm(): ClientPageForm {
    return { id: 0, page_name: '', page_key: '', url: '', parent_id: 0 };
  }

  async loadPages(): Promise<void> {
    this.isLoading = true;
    try {
      const list = await firstValueFrom(this.permissionsService.getClientPagesRaw());
      this.pages = (list || []).sort((a, b) => Number(a.id) - Number(b.id));
    } catch (err: any) {
      console.warn('[ClientPagesComponent] Failed to load client pages:', err);
      this.pages = [];
      this.sharedService.createNotification('error', this.extractError(err, this.t('err-load')));
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private matchesTerm(p: ClientPageItem, term: string): boolean {
    return (p.page_name || '').toLowerCase().includes(term) ||
      this.displayName(p).toLowerCase().includes(term) ||
      (p.page_key || '').toLowerCase().includes(term) ||
      (p.url || '').toLowerCase().includes(term) ||
      String(p.id ?? '').includes(term);
  }

  /** A page counts as a child only when its parent actually exists in the list. */
  private isChild(p: ClientPageItem): boolean {
    const pid = Number(p.parent_id) || 0;
    return pid > 0 && this.pages.some((x) => Number(x.id) === pid);
  }

  private childrenOf(id: number): ClientPageItem[] {
    return this.pages.filter((p) => Number(p.parent_id) === id);
  }

  /**
   * Rows in tree order: every top-level page followed by its own children.
   * A page whose parent_id points at a missing row is shown at top level so it
   * can never disappear. While searching, the parent of a matched child is kept
   * as context so the hierarchy still reads correctly.
   */
  get displayRows(): PageRow[] {
    const term = (this.searchTerm || '').toLowerCase().trim();

    let visible: Set<number>;
    if (term) {
      visible = new Set<number>();
      for (const p of this.pages) {
        if (!this.matchesTerm(p, term)) continue;
        visible.add(Number(p.id));
        if (this.isChild(p)) visible.add(Number(p.parent_id));
      }
    } else {
      visible = new Set(this.pages.map((p) => Number(p.id)));
    }

    const tops = this.pages
      .filter((p) => !this.isChild(p))
      .sort((a, b) => Number(a.id) - Number(b.id));

    const rows: PageRow[] = [];
    for (const top of tops) {
      const topId = Number(top.id);
      const allKids = this.childrenOf(topId).sort((a, b) => Number(a.id) - Number(b.id));
      const shownKids = allKids.filter((k) => visible.has(Number(k.id)));

      // keep a parent that does not match itself only when a child of it does
      if (!visible.has(topId) && shownKids.length === 0) continue;

      rows.push({ page: top, level: 0, childCount: allKids.length, isLastChild: false });

      if (this.collapsedParents.has(topId)) continue;
      shownKids.forEach((kid, i) => {
        rows.push({ page: kid, level: 1, childCount: 0, isLastChild: i === shownKids.length - 1 });
      });
    }
    return rows;
  }

  toggleCollapse(id: number | undefined, event?: MouseEvent): void {
    event?.stopPropagation();
    const key = Number(id);
    if (this.collapsedParents.has(key)) {
      this.collapsedParents.delete(key);
    } else {
      this.collapsedParents.add(key);
    }
  }

  isCollapsed(id: number | undefined): boolean {
    return this.collapsedParents.has(Number(id));
  }

  get hasAnyChildren(): boolean {
    return this.pages.some((p) => this.isChild(p));
  }

  get allCollapsed(): boolean {
    const parents = this.pages.filter((p) => this.childrenOf(Number(p.id)).length > 0);
    return parents.length > 0 && parents.every((p) => this.collapsedParents.has(Number(p.id)));
  }

  toggleAll(): void {
    if (this.allCollapsed) {
      this.collapsedParents.clear();
      return;
    }
    this.collapsedParents = new Set(
      this.pages.filter((p) => this.childrenOf(Number(p.id)).length > 0).map((p) => Number(p.id))
    );
  }

  /** Pages selectable as parent (everything except the page being edited). */
  get parentOptions(): ClientPageItem[] {
    return this.pages.filter((p) => Number(p.id) !== Number(this.form.id));
  }

  getParentName(parentId: number | null | undefined): string {
    if (!parentId) return '';
    const parent = this.pages.find((p) => Number(p.id) === Number(parentId));
    return parent ? this.displayName(parent) : `#${parentId}`;
  }

  // ── Create / Edit ─────────────────────────────────────────────
  openCreateModal(): void {
    this.isEditMode = false;
    this.form = this.emptyForm();
    this.formErrors = {};
    this.serverError = '';
    this.keyTouched = false;
    this.isFormModalVisible = true;
  }

  openEditModal(page: ClientPageItem, event?: MouseEvent): void {
    event?.stopPropagation();
    this.isEditMode = true;
    this.form = {
      id: Number(page.id),
      page_name: page.page_name || '',
      page_key: page.page_key || '',
      url: page.url || '',
      parent_id: Number(page.parent_id) || 0,
    };
    this.formErrors = {};
    this.serverError = '';
    this.keyTouched = true;
    this.isFormModalVisible = true;
  }

  closeFormModal(): void {
    this.isFormModalVisible = false;
    this.formErrors = {};
    this.serverError = '';
  }

  onFieldInput(field: keyof ClientPageForm): void {
    if (this.formErrors[field]) delete this.formErrors[field];
    if (this.serverError) this.serverError = '';
  }

  /** Auto-fill page_key from page_name on create until the key is edited manually. */
  onPageNameInput(): void {
    this.onFieldInput('page_name');
    if (!this.isEditMode && !this.keyTouched) {
      this.form.page_key = this.toSnakeKey(this.form.page_name);
    }
  }

  onPageKeyInput(): void {
    this.keyTouched = true;
    this.form.page_key = this.toSnakeKey(this.form.page_key, true);
    this.onFieldInput('page_key');
  }

  private toSnakeKey(value: string, keepTrailing = false): string {
    let key = (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9_\s-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_');
    if (!keepTrailing) key = key.replace(/^_|_$/g, '');
    return key;
  }

  private validate(): boolean {
    const errors: Partial<Record<keyof ClientPageForm, string>> = {};
    const key = this.form.page_key.trim();
    const url = this.form.url.trim();

    if (!this.form.page_name.trim()) errors.page_name = this.t('err-name-required');

    if (!key) {
      errors.page_key = this.t('err-key-required');
    } else if (!/^[a-z0-9_]+$/.test(key)) {
      errors.page_key = this.t('err-key-format');
    } else {
      const dup = this.pages.find(
        (p) => (p.page_key || '').toLowerCase() === key.toLowerCase() && Number(p.id) !== Number(this.form.id)
      );
      if (dup) errors.page_key = this.t('err-key-duplicate', { name: dup.page_name });
    }

    if (!url) {
      errors.url = this.t('err-url-required');
    } else if (!url.startsWith('/')) {
      errors.url = this.t('err-url-format');
    }

    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean {
    return !!this.form.page_name.trim() && !!this.form.page_key.trim() && !!this.form.url.trim();
  }

  async handleSave(): Promise<void> {
    if (!this.validate()) return;
    this.isSaving = true;
    this.serverError = '';
    const name = this.form.page_name.trim();

    try {
      if (this.isEditMode) {
        await firstValueFrom(this.permissionsService.updateClientPage(this.form).pipe(timeout(8000)));
        this.sharedService.createNotification('success', this.t('msg-updated', { name }));
      } else {
        await firstValueFrom(this.permissionsService.createClientPage(this.form).pipe(timeout(8000)));
        this.sharedService.createNotification('success', this.t('msg-created', { name }));
      }
      this.closeFormModal();
      await this.loadPages();
    } catch (err: any) {
      console.warn('[ClientPagesComponent] save error:', err);
      this.serverError = this.extractError(err, this.t(this.isEditMode ? 'err-update' : 'err-create'));
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  // ── Delete ────────────────────────────────────────────────────
  openDeleteModal(page: ClientPageItem, event?: MouseEvent): void {
    event?.stopPropagation();
    this.deletingPage = page;
    this.isDeleteModalVisible = true;
  }

  closeDeleteModal(): void {
    this.isDeleteModalVisible = false;
    this.deletingPage = null;
  }

  get deletingChildrenCount(): number {
    if (!this.deletingPage) return 0;
    const id = Number(this.deletingPage.id);
    return this.pages.filter((p) => Number(p.parent_id) === id).length;
  }

  async handleDelete(): Promise<void> {
    if (!this.deletingPage) return;
    const page = this.deletingPage;
    this.isDeleting = true;
    try {
      await firstValueFrom(this.permissionsService.deleteClientPage(page.id as number).pipe(timeout(8000)));
      this.sharedService.createNotification('success', this.t('msg-deleted', { name: this.displayName(page) }));
      this.pages = this.pages.filter((p) => Number(p.id) !== Number(page.id));
      this.closeDeleteModal();
    } catch (err: any) {
      console.warn('[ClientPagesComponent] delete error:', err);
      this.sharedService.createNotification('error', this.extractError(err, this.t('err-delete')));
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  // ── Import default pages ──────────────────────────────────────
  private normKey(key: string | undefined | null): string {
    return (key || '').toLowerCase().trim().replace(/-/g, '_');
  }

  /** Default pages (from the service fallback list) not yet present in the backend, matched by page_key. */
  get missingDefaultPages(): ClientPageItem[] {
    const existing = new Set(this.pages.map((p) => this.normKey(p.page_key)));
    return this.permissionsService.getFallbackClientPages().filter((p) => !existing.has(this.normKey(p.page_key)));
  }

  openImportModal(): void {
    this.importProgress = '';
    this.isImportModalVisible = true;
  }

  closeImportModal(): void {
    if (this.isImporting) return;
    this.isImportModalVisible = false;
  }

  /**
   * Creates all missing default pages. Parents are created first so children can
   * reference the real backend id returned by POST /client_pages.
   */
  async handleImportDefaults(): Promise<void> {
    const defaults = this.permissionsService.getFallbackClientPages();
    const missing = this.missingDefaultPages;
    if (missing.length === 0) {
      this.closeImportModal();
      return;
    }

    this.isImporting = true;
    // fallback id -> real backend id (pre-filled with pages that already exist)
    const idMap = new Map<number, number>();
    for (const d of defaults) {
      const found = this.pages.find((p) => this.normKey(p.page_key) === this.normKey(d.page_key));
      if (found) idMap.set(Number(d.id), Number(found.id));
    }

    const parentsFirst = [...missing].sort((a, b) => (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0));
    let created = 0;
    const failed: string[] = [];

    for (let i = 0; i < parentsFirst.length; i++) {
      const d = parentsFirst[i];
      this.importProgress = `${i + 1} / ${parentsFirst.length}  ${d.page_name}`;
      this.cdr.detectChanges();
      try {
        const res: any = await firstValueFrom(
          this.permissionsService.createClientPage({
            page_name: d.page_name || '',
            page_key: d.page_key || '',
            url: d.url || '',
            parent_id: d.parent_id ? (idMap.get(Number(d.parent_id)) || 0) : 0,
          }).pipe(timeout(10000))
        );
        // POST /client_pages responds { success, action, page: { id, ... } }
        const newId = Number(res?.page?.id ?? res?.id ?? res?.data?.id ?? res?.result?.id ?? 0);
        if (newId) idMap.set(Number(d.id), newId);
        created++;
      } catch (err: any) {
        console.warn('[ClientPagesComponent] import failed for', d.page_key, err);
        failed.push(d.page_name || d.page_key || '');
      }
    }

    this.isImporting = false;
    this.isImportModalVisible = false;
    if (created > 0) {
      this.sharedService.createNotification('success', this.t('msg-imported', { count: created }));
    }
    if (failed.length > 0) {
      this.sharedService.createNotification('error', this.t('err-import', { names: failed.join(', ') }));
    }
    await this.loadPages();
  }

  private extractError(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.error || err?.message || fallback;
  }
}
